/**
 * Compliance dashboard — NIL Go submissions (US-033.5).
 *
 * Org-side view of every NilGoSubmission row from athletes in the
 * compliance officer's org(s). Pairs with the verification queue from
 * US-031.4 — together they cover the two big "what's the state of my
 * athletes' compliance" questions.
 *
 * Filters: status, athlete, payor, affiliation, date range, over-$600.
 * Each row links to the read-only snapshot at /compliance/submissions/<id>.
 * CSV export button uses the same filter set and downloads via a blob.
 *
 * Defers PDF export to v2 (CSV is the heavy-use one — Excel for records
 * requests; PDF needs a server-side renderer dep).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@app/ui';
import { formatCurrency, formatDate } from '@app/validators';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { api, client } from '../../../../lib/api';

type SubmissionStatus =
  | 'prepared'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'needs_revision'
  | 'rejected'
  | 'cancelled';

type Affiliation = 'unknown' | 'associated' | 'non_associated';

interface FilterState {
  status?: SubmissionStatus;
  athleteId?: string;
  payorId?: string;
  affiliation?: Affiliation;
  overThreshold?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

const STATUS_STYLES: Record<SubmissionStatus, { cls: string; label: string }> = {
  prepared: { cls: 'bg-muted text-muted-foreground border-border', label: 'Prepared' },
  submitted: { cls: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Submitted' },
  under_review: { cls: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Under review' },
  approved: { cls: 'bg-green-100 text-green-800 border-green-200', label: 'Approved' },
  needs_revision: { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Needs revision' },
  rejected: { cls: 'bg-red-100 text-red-800 border-red-200', label: 'Rejected' },
  cancelled: { cls: 'bg-muted text-muted-foreground border-border', label: 'Cancelled' },
};

export const Route = createFileRoute('/_authenticated/compliance/submissions/')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../../../lib/api-server');
    const serverApi = await getServerApi();
    await Promise.all([
      context.queryClient.ensureQueryData(
        serverApi.compliance.submissions.queryOptions({ input: { page: 1, limit: 25 } }),
      ),
      context.queryClient.ensureQueryData(serverApi.compliance.filterOptions.queryOptions()),
    ]);
  },
  component: SubmissionsPage,
});

function SubmissionsPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterState>({});
  const [exporting, setExporting] = useState(false);

  const { data: opts } = useSuspenseQuery(api.compliance.filterOptions.queryOptions());
  const queryInput = useMemo(() => ({ page, limit: 25, ...filters }), [page, filters]);
  const { data } = useSuspenseQuery(api.compliance.submissions.queryOptions({ input: queryInput }));
  const { data: stats } = useQuery(
    api.compliance.submissionsStats.queryOptions({ input: filters }),
  );

  const updateFilter = (patch: Partial<FilterState>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({});
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await client.compliance.submissionsExportCsv(filters);
      // Defensive: coerce to a string so the blob can't end up holding
      // the entire envelope if oRPC's encoding ever changes shape — the
      // user-visible symptom is "downloaded file is JSON, not CSV".
      const csvText =
        typeof result?.csv === 'string'
          ? result.csv
          : typeof result === 'string'
            ? result
            : JSON.stringify(result);
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nilboard-nil-go-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        result.truncated
          ? `Exported ${result.rowCount} rows (truncated at the 5000-row cap)`
          : `Exported ${result.rowCount} rows`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">NIL Go submissions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every submission from athletes in your org{' '}
            {stats ? `· ${stats.total} total · ${stats.overThreshold} ≥ $600` : ''}
          </p>
        </div>
        <Button onClick={handleExport} disabled={exporting || data.rows.length === 0}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">Filters</CardTitle>
            {Object.keys(filters).length > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Clear
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterSelect
              label="Status"
              value={filters.status ?? ''}
              onChange={(v) =>
                updateFilter({ status: (v || undefined) as SubmissionStatus | undefined })
              }
              placeholder="Any"
              options={[
                { value: 'submitted', label: 'Submitted' },
                { value: 'under_review', label: 'Under review' },
                { value: 'approved', label: 'Approved' },
                { value: 'needs_revision', label: 'Needs revision' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
            <FilterSelect
              label="Athlete"
              value={filters.athleteId ?? ''}
              onChange={(v) => updateFilter({ athleteId: v || undefined })}
              placeholder="Any"
              options={opts.athletes.map((a) => ({ value: a.id, label: a.name }))}
            />
            <FilterSelect
              label="Payor"
              value={filters.payorId ?? ''}
              onChange={(v) => updateFilter({ payorId: v || undefined })}
              placeholder="Any"
              options={opts.payors.map((p) => ({ value: p.id, label: p.legalName }))}
            />
            <FilterSelect
              label="Affiliation"
              value={filters.affiliation ?? ''}
              onChange={(v) =>
                updateFilter({ affiliation: (v || undefined) as Affiliation | undefined })
              }
              placeholder="Any"
              options={[
                { value: 'associated', label: 'Associated' },
                { value: 'non_associated', label: 'Non-associated' },
                { value: 'unknown', label: 'Unknown' },
              ]}
            />
            <FilterSelect
              label="Threshold"
              value={
                filters.overThreshold === true
                  ? 'over'
                  : filters.overThreshold === false
                    ? 'under'
                    : ''
              }
              onChange={(v) =>
                updateFilter({
                  overThreshold: v === 'over' ? true : v === 'under' ? false : undefined,
                })
              }
              placeholder="Any"
              options={[
                { value: 'over', label: '≥ $600' },
                { value: 'under', label: '< $600' },
              ]}
            />
            <div className="space-y-1">
              <Label className="text-xs">Submitted from</Label>
              <Input
                type="date"
                value={filters.dateFrom ?? ''}
                onChange={(e) => updateFilter({ dateFrom: e.target.value || undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Submitted to</Label>
              <Input
                type="date"
                value={filters.dateTo ?? ''}
                onChange={(e) => updateFilter({ dateTo: e.target.value || undefined })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {data.rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            No submissions match these filters yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <Th>Athlete</Th>
                  <Th>Deal</Th>
                  <Th>Payor</Th>
                  <Th align="right">Amount</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th>Decision</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const style = STATUS_STYLES[r.status as SubmissionStatus];
                  return (
                    <tr key={r.id} className="border-t">
                      <Td>
                        <div className="font-medium">{r.athlete.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[r.athlete.sport, r.athlete.school].filter(Boolean).join(' · ')}
                        </div>
                      </Td>
                      <Td>
                        <div className="font-medium">{r.deal.title}</div>
                        <div className="text-xs text-muted-foreground">{r.deal.brandName}</div>
                      </Td>
                      <Td>
                        {r.deal.payor ? (
                          <>
                            <div>{r.deal.payor.legalName}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.deal.payor.affiliation.replace(/_/g, '-')}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td align="right">
                        <div className="font-medium">{formatCurrency(r.deal.amount)}</div>
                        {r.deal.amount < 600 && (
                          <div className="text-xs text-amber-700">&lt; $600</div>
                        )}
                      </Td>
                      <Td>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${style.cls}`}
                        >
                          {style.label}
                        </span>
                        {r.revisionNumber > 1 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            rev #{r.revisionNumber}
                          </span>
                        )}
                      </Td>
                      <Td>
                        <span className="text-xs">
                          {r.submittedAt ? formatDate(r.submittedAt) : '—'}
                        </span>
                      </Td>
                      <Td>
                        <span className="text-xs">
                          {r.decidedAt ? formatDate(r.decidedAt) : '—'}
                        </span>
                        {r.decisionReason && (
                          <div
                            className="text-xs text-muted-foreground truncate max-w-[200px]"
                            title={r.decisionReason}
                          >
                            {r.decisionReason}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <Link
                          to="/compliance/submissions/$submissionId"
                          params={{ submissionId: r.id }}
                          className="text-xs underline text-primary"
                        >
                          View
                        </Link>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {data.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {data.totalPages} · {data.total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
}) {
  // shadcn's Select doesn't allow an empty-string value, so we use a
  // sentinel and translate at the boundary.
  const SENTINEL = '__any__';
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select
        value={value === '' ? SENTINEL : value}
        onValueChange={(v) => onChange(v === SENTINEL ? '' : v)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SENTINEL}>Any</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
      scope="col"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <td className={`px-3 py-3 align-top ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </td>
  );
}
